/**
 * Component 6: PDCA Cycles
 * Dynamic table with add/edit/delete rows, priority levels, assignments, dates, status tracking
 */

import React, { useState, useEffect } from 'react';
import {
  Plus as PlusIcon,
  Edit3 as EditIcon,
  Trash2 as TrashIcon,
  Save as SaveIcon,
  X as XIcon,
  AlertCircle as AlertCircleIcon
} from 'lucide-react';

import type { KATABoardV2, PDCACycleV2, PDCACycleV2Create, PDCACycleV2Update, User as KataUser } from '../../../types/kataBoardV2';
import { pdcaCycleV2Api, fileAttachmentApi } from '../../../services/kataBoardV2';
import PDCACycleAttachments, { PDCACycleAttachmentLinks } from './PDCACycleAttachments';
import { useAlert } from '../../../hooks/useAlert';
import SearchableUserDropdown from '../../common/SearchableUserDropdown';
import { User, UserService } from '../../../services/user';
import EnhancedPagination from '../../common/EnhancedPagination';
import { useKataBoardCanEdit } from '../../../contexts/KataBoardAccessContext';

interface Component06Props {
  kataBoard: KATABoardV2;
}

const Component06_PDCACycles: React.FC<Component06Props> = ({ kataBoard }) => {
  const { confirmDelete, AlertComponent } = useAlert();
  const canEdit = useKataBoardCanEdit();

  // State management
  const [cycles, setCycles] = useState<PDCACycleV2[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);

  // Summary statistics state (for ALL cycles, not just current page)
  const [summaryStats, setSummaryStats] = useState({
    total: 0,
    inProgress: 0,
    completed: 0,
    overdue: 0,
    dueSoon: 0,
    withTargetDates: 0,
    assigned: 0,
    unassigned: 0
  });

  // Local editing state
  const [localEditData, setLocalEditData] = useState<Partial<PDCACycleV2> | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [editFiles, setEditFiles] = useState<File[]>([]);
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState<number[]>([]);

  // Form state for new cycle
  const [newCycle, setNewCycle] = useState<Partial<PDCACycleV2Create>>({
    kata_board: kataBoard.id,
    priority: 'medium',
    action_required: '',
    root_cause: '',
    estimated_gain: '',
    comments: '',
    responsible_lead: null,
    assigned_date: null,
    target_date: null
  });

  // Helper function to check if cycle is overdue
  const isOverdue = (cycle: PDCACycleV2): boolean => {
    if (!cycle.target_date || cycle.status === 'completed' || cycle.status === 'cancelled') {
      return false;
    }
    const targetDate = new Date(cycle.target_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    return targetDate < today;
  };

  // Helper function to check if cycle is due soon (within 3 days)
  const isDueSoon = (cycle: PDCACycleV2): boolean => {
    if (!cycle.target_date || cycle.status === 'completed' || cycle.status === 'cancelled') {
      return false;
    }
    const targetDate = new Date(cycle.target_date);
    const today = new Date();
    const threeDaysFromNow = new Date(today.getTime() + (3 * 24 * 60 * 60 * 1000));
    return targetDate <= threeDaysFromNow && targetDate >= today;
  };

  // Function to fetch and calculate summary statistics for ALL cycles
  const fetchSummaryStatistics = async () => {
    try {
      // Fetch ALL cycles without pagination
      const allCyclesResponse = await pdcaCycleV2Api.getAll({ kata_board: kataBoard.id }, 1, 10000);
      const allCycles = allCyclesResponse.results || [];

      // Calculate statistics
      setSummaryStats({
        total: allCycles.length,
        inProgress: allCycles.filter(c => c.status === 'in_progress').length,
        completed: allCycles.filter(c => c.status === 'completed').length,
        overdue: allCycles.filter(c => isOverdue(c)).length,
        dueSoon: allCycles.filter(c => isDueSoon(c)).length,
        withTargetDates: allCycles.filter(c => c.target_date).length,
        assigned: allCycles.filter(c => c.responsible_lead).length,
        unassigned: allCycles.filter(c => !c.responsible_lead).length
      });
    } catch (error) {
      console.error('Failed to fetch summary statistics:', error);
    }
  };

  // Load PDCA cycles and users
  useEffect(() => {
    const loadData = async () => {
      if (!kataBoard.id) return;

      try {
        setLoading(true);
        setError(null);

        // Load cycles, users, and summary statistics in parallel
        const [cyclesResponse, usersResponse] = await Promise.all([
          pdcaCycleV2Api.getAll({ kata_board: kataBoard.id }, currentPage, pageSize),
          UserService.getAllDirectoryUsers(),
          fetchSummaryStatistics() // Fetch summary stats
        ]);

        setCycles(cyclesResponse.results || []);
        setTotalCount(cyclesResponse.count || 0);
        setHasNext(cyclesResponse.next !== null);
        setHasPrevious(cyclesResponse.previous !== null);
        setUsers(usersResponse);
      } catch (error) {
        console.error('Failed to load data:', error);
        setError('Failed to load data');
        setCycles([]);
        setTotalCount(0);
        setHasNext(false);
        setHasPrevious(false);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [kataBoard.id, currentPage, pageSize]);

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  // Handle adding new cycle
  const handleAddCycle = async () => {
    if (!newCycle.action_required?.trim()) {
      setError('Action Required is mandatory');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const createdCycle = await pdcaCycleV2Api.create({
        ...newCycle,
        kata_board: kataBoard.id,
        action_required: newCycle.action_required!,
        ...(newFiles.length > 0 ? { attachment_files: newFiles } : {}),
        assigned_date: newCycle.assigned_date || getTodayString()
      } as PDCACycleV2Create);

      setCycles(prev => [...prev, createdCycle]);
      setTotalCount(prev => prev + 1);
      setNewCycle({
        kata_board: kataBoard.id,
        priority: 'medium',
        action_required: '',
        root_cause: '',
        estimated_gain: '',
        comments: '',
        responsible_lead: null,
        assigned_date: null,
        target_date: null
      });
      setShowAddForm(false);
      setShowAddModal(false);
      setNewFiles([]);
      // A failed refresh must not offer to create the same cycle a second time.
      try {
        const response = await pdcaCycleV2Api.getAll({ kata_board: kataBoard.id }, currentPage, pageSize);
        setCycles(response.results || []);
        setTotalCount(response.count || 0);
        setHasNext(response.next !== null);
        setHasPrevious(response.previous !== null);
        await fetchSummaryStatistics();
      } catch {
        setError('The cycle and its attachments were created, but the list could not refresh. Reload to see the latest list.');
      }
    } catch (error) {
      console.error('Failed to create PDCA cycle:', error);
      setError('Failed to create PDCA cycle');
    } finally {
      setLoading(false);
    }
  };

  // Handle starting edit mode
  const handleStartEdit = (cycle: PDCACycleV2) => {
    if (!canEdit) return;
    setEditingId(cycle.id);
    setLocalEditData({ ...cycle });
    setEditFiles([]);
    setRemovedAttachmentIds([]);
    setError(null);
    setHasUnsavedChanges(false);
    setShowEditModal(true);
  };

  // Handle local edit changes
  const handleLocalEdit = (field: string, value: any) => {
    if (!localEditData) return;

    setLocalEditData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasUnsavedChanges(true);
  };

  // Handle saving changes
  const handleSaveChanges = async () => {
    if (!localEditData || !editingId) return;

    try {
      setLoading(true);
      setError(null);

      const updates: Partial<PDCACycleV2Update> = {
        priority: localEditData.priority,
        responsible_lead: localEditData.responsible_lead,
        action_required: localEditData.action_required,
        root_cause: localEditData.root_cause,
        estimated_gain: localEditData.estimated_gain,
        assigned_date: localEditData.assigned_date,
        target_date: localEditData.target_date,
        completed_date: localEditData.completed_date,
        status: localEditData.status
      };

      let updatedCycle = await pdcaCycleV2Api.update(editingId, updates);
      setCycles(prev => prev.map(cycle =>
        cycle.id === editingId ? updatedCycle : cycle
      ));

      const updateAttachments = (attachments: NonNullable<PDCACycleV2['attachments']>) => {
        updatedCycle = { ...updatedCycle, attachments };
        setLocalEditData(prev => prev ? { ...prev, attachments } : prev);
        setCycles(prev => prev.map(cycle => cycle.id === editingId ? { ...cycle, attachments } : cycle));
      };
      // Upload replacements before deleting originals; retain successful changes on retry.
      for (const file of editFiles) {
        const attachment = await fileAttachmentApi.create({
          kata_board: kataBoard.id,
          pdca_cycle: editingId,
          attachment_type: 'pdca_file',
          file,
        });
        updateAttachments([...(updatedCycle.attachments || []), attachment]);
        setEditFiles(prev => prev.filter(pending => pending !== file));
      }
      for (const attachmentId of removedAttachmentIds) {
        await fileAttachmentApi.delete(attachmentId);
        updateAttachments((updatedCycle.attachments || []).filter(attachment => attachment.id !== attachmentId));
        setRemovedAttachmentIds(prev => prev.filter(id => id !== attachmentId));
      }

      // Refresh summary statistics
      await fetchSummaryStatistics();

      // Exit edit mode
      setEditingId(null);
      setLocalEditData(null);
      setHasUnsavedChanges(false);
      setShowEditModal(false);
    } catch (error) {
      console.error('Failed to update PDCA cycle:', error);
      setError('Could not save all changes. Successful changes are saved; retry to apply the remaining changes.');
    } finally {
      setLoading(false);
    }
  };

  // Handle canceling edit
  const handleCancelEdit = () => {
    if (loading) return;
    setEditingId(null);
    setLocalEditData(null);
    setHasUnsavedChanges(false);
    setShowEditModal(false);
    setEditFiles([]);
    setRemovedAttachmentIds([]);
    setError(null);
  };

  // Handle canceling add
  const handleCancelAdd = () => {
    if (loading) return;
    setNewFiles([]);
    setError(null);
    setNewCycle({
      kata_board: kataBoard.id,
      priority: 'medium',
      action_required: '',
      root_cause: '',
      estimated_gain: '',
      comments: '',
      responsible_lead: null,
      assigned_date: null,
      target_date: null
    });
    setShowAddModal(false);
  };

  // Handle deleting cycle
  const handleDeleteCycle = async (id: number) => {
    console.log('handleDeleteCycle called with:', id, typeof id);
    if (!id) {
      console.error('Cannot delete cycle: ID is missing');
      setError('Cannot delete cycle: Invalid ID');
      return;
    }

    confirmDelete('this PDCA cycle', async () => {
      try {
        setLoading(true);
        setError(null);
        await pdcaCycleV2Api.delete(id);

        // Refresh the cycles data to maintain pagination state
        const updatedCyclesResponse = await pdcaCycleV2Api.getAll({ kata_board: kataBoard.id }, currentPage, pageSize);
        setCycles(updatedCyclesResponse.results || []);
        setTotalCount(updatedCyclesResponse.count || 0);
        setHasNext(updatedCyclesResponse.next !== null);
        setHasPrevious(updatedCyclesResponse.previous !== null);

        // Refresh summary statistics
        await fetchSummaryStatistics();

        // If current page becomes empty and not on first page, go to previous page
        if (updatedCyclesResponse.results.length === 0 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        }
      } catch (error) {
        console.error('Failed to delete PDCA cycle:', error);
        setError('Failed to delete PDCA cycle');
      } finally {
        setLoading(false);
      }
    });
  };

  // Priority configuration
  const priorityConfig = {
    high: { color: 'red', icon: '🔴', label: 'High', bgColor: 'bg-red-100', textColor: 'text-red-800' },
    medium: { color: 'yellow', icon: '🟡', label: 'Medium', bgColor: 'bg-yellow-100', textColor: 'text-yellow-800' },
    low: { color: 'green', icon: '🟢', label: 'Low', bgColor: 'bg-green-100', textColor: 'text-green-800' }
  };

  // Status configuration
  const statusConfig = {
    assigned: { color: 'blue', icon: '📋', label: 'Assigned', bgColor: 'bg-blue-100', textColor: 'text-blue-800' },
    in_progress: { color: 'orange', icon: '⏳', label: 'In Progress', bgColor: 'bg-orange-100', textColor: 'text-orange-800' },
    completed: { color: 'green', icon: '✅', label: 'Completed', bgColor: 'bg-green-100', textColor: 'text-green-800' },
    overdue: { color: 'red', icon: '⚠️', label: 'Overdue', bgColor: 'bg-red-100', textColor: 'text-red-800' },
    cancelled: { color: 'gray', icon: '❌', label: 'Cancelled', bgColor: 'bg-gray-100', textColor: 'text-gray-800' }
  };

  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayString = (): string => {
    return new Date().toISOString().split('T')[0];
  };

  // Handle responsible lead selection change
  const handleResponsibleLeadChange = (selectedIds: number[]) => {
    setNewCycle(prev => ({
      ...prev,
      responsible_lead: selectedIds.length > 0 ? selectedIds[0] : null
    }));
  };

  // Handle responsible lead selection change in edit form
  const handleEditResponsibleLeadChange = (selectedIds: number[]) => {
    handleLocalEdit('responsible_lead', selectedIds.length > 0 ? selectedIds[0] : null);
  };


  // Render table row (view mode only)
  const renderCycleRow = (cycle: PDCACycleV2, index: number) => {
    const priority = priorityConfig[cycle.priority || 'medium'];
    const status = statusConfig[cycle.status || 'assigned'];

    return (
      <tr key={cycle.id} className="hover:bg-gray-50">
        {/* Index */}
        <td className="px-3 py-4 text-sm text-gray-900 font-medium">
          {index + 1}
        </td>

        {/* Priority */}
        <td className="px-3 py-4 text-sm">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${priority.bgColor} ${priority.textColor}`}>
            {priority.icon} {priority.label}
          </span>
        </td>

        {/* Action Required */}
        <td className="px-3 py-4 text-sm">
          <div className="max-w-xs">
            <p className="text-gray-900 truncate" title={cycle.action_required}>
              {cycle.action_required}
            </p>
            {(cycle.attachments || []).length > 0 && (
              <div className="mt-2"><PDCACycleAttachmentLinks attachments={cycle.attachments!} /></div>
            )}
          </div>
        </td>

        {/* Root Cause */}
        <td className="px-3 py-4 text-sm">
          <div className="max-w-xs">
            <p className="text-gray-900 truncate" title={cycle.root_cause || ''}>
              {cycle.root_cause || '-'}
            </p>
          </div>
        </td>

        {/* Estimated Gain */}
        <td className="px-3 py-4 text-sm">
          <p className="text-gray-900">
            {cycle.estimated_gain || '-'}
          </p>
        </td>

        {/* Responsible Lead */}
        <td className="px-3 py-4 text-sm">
          <div className="flex items-center">
            {cycle.responsible_lead_info ? (
              <>
                <div className="flex-shrink-0 h-8 w-8">
                  <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                    <span className="text-xs font-medium text-blue-800">
                      {cycle.responsible_lead_info.first_name?.[0]}{cycle.responsible_lead_info.last_name?.[0]}
                    </span>
                  </div>
                </div>
                <div className="ml-2">
                  <p className="text-sm font-medium text-gray-900">
                    {cycle.responsible_lead_info.full_name || `${cycle.responsible_lead_info.first_name} ${cycle.responsible_lead_info.last_name}`.trim()}
                  </p>
                </div>
              </>
            ) : (
              <span className="text-gray-400 text-sm">Unassigned</span>
            )}
          </div>
        </td>

        {/* Assigned Date */}
        <td className="px-3 py-4 text-sm">
          <p className="text-gray-900">
            {cycle.assigned_date ? new Date(cycle.assigned_date).toLocaleDateString() : '-'}
          </p>
        </td>

        {/* Target Date */}
        <td className="px-3 py-4 text-sm">
          <div>
            <p className={`text-sm ${isOverdue(cycle) ? 'text-red-600 font-medium' : 'text-gray-900'}`}>
              {cycle.target_date ? new Date(cycle.target_date).toLocaleDateString() : '-'}
            </p>
            {isOverdue(cycle) && (
              <p className="text-xs text-red-500">Overdue</p>
            )}
          </div>
        </td>

        {/* Completed Date */}
        <td className="px-3 py-4 text-sm">
          <p className="text-gray-900">
            {cycle.completed_date ? new Date(cycle.completed_date).toLocaleDateString() : '-'}
          </p>
        </td>

        {/* Status */}
        <td className="px-3 py-4 text-sm">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.bgColor} ${status.textColor}`}>
            {status.icon} {status.label}
          </span>
        </td>

        {/* Actions */}
        {canEdit && (
        <td className="px-3 py-4 text-sm text-right">
          <div className="flex items-center justify-end space-x-2">
            <button
              onClick={() => handleStartEdit(cycle)}
              className="text-blue-600 hover:text-blue-800 focus:outline-none"
              title="Edit cycle"
            >
              <EditIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => handleDeleteCycle(cycle.id)}
              className="text-red-600 hover:text-red-800 focus:outline-none"
              title="Delete cycle"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        </td>
        )}
      </tr>
    );
  };


  if (loading && cycles.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-600 rounded-lg p-2">
              <AlertCircleIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-blue-900">PDCA Cycles</h3>
              <p className="text-blue-700 text-sm">Plan-Do-Check-Act improvement cycles</p>
            </div>
          </div>
          {canEdit && (
          <button
            onClick={() => { setError(null); setShowAddModal(true); }}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Cycle
          </button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertCircleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                {error}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PDCA Table */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  #
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action Required
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Root Cause
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Est. Gain
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Responsible
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Assigned
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Target
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Completed
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                {canEdit && (
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {/* Existing cycles */}
              {cycles.map((cycle, index) => renderCycleRow(cycle, index))}


              {/* Empty state */}
              {cycles.length === 0 && (
                <tr>
                  <td colSpan={canEdit ? 11 : 10} className="px-6 py-12 text-center">
                    <AlertCircleIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No PDCA Cycles</h3>
                    <p className="text-gray-500 mb-4">Get started by creating your first improvement cycle</p>
                    {canEdit && (
                    <button
                      onClick={() => setShowAddModal(true)}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <PlusIcon className="h-4 w-4 mr-2" />
                      Add First Cycle
                    </button>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalCount > 0 && (
          <EnhancedPagination
            currentPage={currentPage}
            totalCount={totalCount}
            pageSize={pageSize}
            hasNext={hasNext}
            hasPrevious={hasPrevious}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={[10, 20, 50, 100]}
            showPageSizeSelector={true}
            showPageNumbers={true}
          />
        )}
      </div>

      {/* Summary */}
      {summaryStats.total > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h4 className="text-lg font-medium text-gray-900 mb-4">Cycles Summary</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">
                {summaryStats.total}
              </div>
              <div className="text-sm text-blue-800">Total Cycles</div>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-orange-600">
                {summaryStats.inProgress}
              </div>
              <div className="text-sm text-orange-800">In Progress</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {summaryStats.completed}
              </div>
              <div className="text-sm text-green-800">Completed</div>
            </div>
          </div>

          {/* Second row with date-related statistics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div className="bg-red-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-red-600">
                {summaryStats.overdue}
              </div>
              <div className="text-sm text-red-800">Overdue</div>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">
                {summaryStats.dueSoon}
              </div>
              <div className="text-sm text-yellow-800">Due Soon (3 days)</div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">
                {summaryStats.withTargetDates}
              </div>
              <div className="text-sm text-purple-800">With Target Dates</div>
            </div>
          </div>

          {/* Third row with assignment statistics */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="bg-indigo-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-indigo-600">
                {summaryStats.assigned}
              </div>
              <div className="text-sm text-indigo-800">Assigned to Users</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-gray-600">
                {summaryStats.unassigned}
              </div>
              <div className="text-sm text-gray-800">Unassigned</div>
            </div>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && canEdit && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            {/* Modal Header */}
            <div className="flex justify-between items-center pb-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Add New PDCA Cycle</h3>
              <button
                onClick={handleCancelAdd}
                className="text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                <XIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="py-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Priority & Status */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Priority
                    </label>
                    <select
                      value={newCycle.priority || 'medium'}
                      onChange={(e) => setNewCycle(prev => ({ ...prev, priority: e.target.value as any }))}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="high">🔴 High</option>
                      <option value="medium">🟡 Medium</option>
                      <option value="low">🟢 Low</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Responsible Lead
                    </label>
                    <SearchableUserDropdown
                      users={users}
                      selectedUsers={newCycle.responsible_lead ? [newCycle.responsible_lead] : []}
                      onSelectionChange={handleResponsibleLeadChange}
                      placeholder="Search and select responsible lead..."
                      multiple={false}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Estimated Gain
                    </label>
                    <input
                      type="text"
                      value={newCycle.estimated_gain || ''}
                      onChange={(e) => setNewCycle(prev => ({ ...prev, estimated_gain: e.target.value }))}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      placeholder="Expected benefit or gain..."
                    />
                  </div>
                </div>

                {/* Dates */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Assigned Date
                    </label>
                    <input
                      type="date"
                      value={newCycle.assigned_date || getTodayString()}
                      onChange={(e) => setNewCycle(prev => ({ ...prev, assigned_date: e.target.value || null }))}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Target Date
                    </label>
                    <input
                      type="date"
                      value={newCycle.target_date || ''}
                      onChange={(e) => setNewCycle(prev => ({ ...prev, target_date: e.target.value || null }))}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      min={newCycle.assigned_date || getTodayString()}
                    />
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">
                      💡 <strong>Tip:</strong> New cycles start with "Assigned" status. Set completion date later when the cycle is finished.
                    </p>
                  </div>
                </div>
              </div>

              {/* Full-width fields */}
              <div className="mt-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Action Required *
                  </label>
                  <textarea
                    value={newCycle.action_required || ''}
                    onChange={(e) => setNewCycle(prev => ({ ...prev, action_required: e.target.value }))}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    rows={3}
                    placeholder="Describe the action that needs to be taken..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Root Cause Analysis
                  </label>
                  <textarea
                    value={newCycle.root_cause || ''}
                    onChange={(e) => setNewCycle(prev => ({ ...prev, root_cause: e.target.value }))}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    rows={3}
                    placeholder="Analyze the root cause of the issue..."
                  />
                </div>
                <PDCACycleAttachments files={newFiles} onFilesChange={setNewFiles} disabled={loading} />
                {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                onClick={handleCancelAdd}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={handleAddCycle}
                disabled={loading || !newCycle.action_required?.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                {loading ? 'Creating...' : 'Create PDCA Cycle'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && canEdit && localEditData && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            {/* Modal Header */}
            <div className="flex justify-between items-center pb-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Edit PDCA Cycle</h3>
              <button
                onClick={handleCancelEdit}
                className="text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                <XIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="py-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Priority & Status */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Priority
                    </label>
                    <select
                      value={localEditData.priority || 'medium'}
                      onChange={(e) => handleLocalEdit('priority', e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="high">🔴 High</option>
                      <option value="medium">🟡 Medium</option>
                      <option value="low">🟢 Low</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      value={localEditData.status || 'assigned'}
                      onChange={(e) => handleLocalEdit('status', e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="assigned">📋 Assigned</option>
                      <option value="in_progress">⏳ In Progress</option>
                      <option value="completed">✅ Completed</option>
                      <option value="overdue">⚠️ Overdue</option>
                      <option value="cancelled">❌ Cancelled</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Responsible Lead
                    </label>
                    <SearchableUserDropdown
                      users={users}
                      selectedUsers={localEditData.responsible_lead ? [localEditData.responsible_lead] : []}
                      onSelectionChange={handleEditResponsibleLeadChange}
                      placeholder="Search and select responsible lead..."
                      multiple={false}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Estimated Gain
                    </label>
                    <input
                      type="text"
                      value={localEditData.estimated_gain || ''}
                      onChange={(e) => handleLocalEdit('estimated_gain', e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      placeholder="Expected benefit or gain..."
                    />
                  </div>
                </div>

                {/* Dates */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Assigned Date
                    </label>
                    <input
                      type="date"
                      value={localEditData.assigned_date || ''}
                      onChange={(e) => handleLocalEdit('assigned_date', e.target.value || null)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Target Date
                    </label>
                    <input
                      type="date"
                      value={localEditData.target_date || ''}
                      onChange={(e) => handleLocalEdit('target_date', e.target.value || null)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      min={localEditData.assigned_date || undefined}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Completed Date
                    </label>
                    <input
                      type="date"
                      value={localEditData.completed_date || ''}
                      onChange={(e) => {
                        handleLocalEdit('completed_date', e.target.value || null);
                        if (e.target.value) {
                          handleLocalEdit('status', 'completed');
                        }
                      }}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      min={localEditData.assigned_date || undefined}
                    />
                  </div>
                </div>
              </div>

              {/* Full-width fields */}
              <div className="mt-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Action Required *
                  </label>
                  <textarea
                    value={localEditData.action_required || ''}
                    onChange={(e) => handleLocalEdit('action_required', e.target.value)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    rows={3}
                    placeholder="Describe the action that needs to be taken..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Root Cause Analysis
                  </label>
                  <textarea
                    value={localEditData.root_cause || ''}
                    onChange={(e) => handleLocalEdit('root_cause', e.target.value)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    rows={3}
                    placeholder="Analyze the root cause of the issue..."
                  />
                </div>
                <PDCACycleAttachments
                  attachments={localEditData.attachments || []}
                  removedIds={removedAttachmentIds}
                  files={editFiles}
                  onFilesChange={setEditFiles}
                  onRemove={id => setRemovedAttachmentIds(prev => [...prev, id])}
                  disabled={loading}
                />
                {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                onClick={handleCancelEdit}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveChanges}
                disabled={loading || !localEditData.action_required?.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Component */}
      {AlertComponent}
    </div>
  );
};

export default Component06_PDCACycles;
